import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import {
  AdminLicense,
  DeviceInfo,
  GenerateLicenseRequest,
  LicenseFeatures,
  LicenseInfo,
  LicensePlatformStats
} from '../models/license.model';

@Injectable({ providedIn: 'root' })
export class LicenseService {
  private readonly api = inject(ApiService);
  private readonly authService = inject(AuthService);

  private readonly CACHE_KEY = 'bf_license_cache';
  private readonly CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days offline grace period

  // ── Signals ──────────────────────────
  license = signal<LicenseInfo | null>(null);
  
  features = computed(() => this.license()?.features);
  
  isValid = computed(() => this.license()?.status?.toLowerCase() === 'active');
  
  plan = computed(() => (this.license()?.plan ?? 'trial').toLowerCase());
  
  daysLeft = computed(() => this.license()?.daysLeft ?? 0);
  
  isLifetime = computed(() => this.daysLeft() === -1);
  
  isExpired = computed(() => this.license()?.status?.toLowerCase() === 'expired');
  
  isTrial = computed(() => this.license()?.plan?.toLowerCase() === 'trial');
  
  isTrialExpired = computed(() => this.license()?.reason === 'TRIAL_EXPIRED');

  // Feature computed signals
  hasAnalytics = computed(() => this.features()?.analytics ?? false);
  hasLoyalty = computed(() => this.features()?.loyalty ?? false);
  hasExportReports = computed(() => this.features()?.exportReports ?? false);
  hasMultiBranch = computed(() => this.features()?.multiBranch ?? false);
  maxStaff = computed(() => this.features()?.maxStaff ?? 2);
  maxTables = computed(() => this.features()?.maxTables ?? 5);

  // ── Methods ──────────────────────────

  hasFeature(feature: keyof LicenseFeatures): boolean {
    return Boolean(this.features()?.[feature] ?? false);
  }

  validateOnStartup(): Observable<void> {
    return this.api.get<LicenseInfo>('/license/my-license').pipe(
      tap(res => {
        this.license.set(res.data);
        this.saveToCache(res.data);
      }),
      map(() => void 0),
      catchError((error) => {
        // On error (offline or server error), try load from cache
        const cached = this.loadFromCache();
        if (cached) {
          this.license.set(cached);
          if (!this.isCacheValid()) {
            console.warn('License cache is older than 7 days, please connect to the internet to verify your license.');
          }
        } else {
          this.license.set(null);
        }
        return of(void 0);
      })
    );
  }

  loadLicense(): Observable<void> {
    return this.api.get<LicenseInfo>('/license/my-license').pipe(
      tap(res => {
        this.license.set(res.data);
        this.saveToCache(res.data);
      }),
      map(() => void 0)
    );
  }

  validateLicenseKey(licenseKey: string, deviceId: string, deviceName: string): Observable<LicenseInfo> {
    return this.api.post<LicenseInfo>('/license/validate', {
      licenseKey,
      deviceId,
      deviceName,
      platform: 'web',
      appVersion: '1.0.0'
    }).pipe(map(res => res.data));
  }

  getDevices(): Observable<DeviceInfo[]> {
    return this.api.get<DeviceInfo[]>('/license/my-devices').pipe(map(res => res.data));
  }

  deactivateDevice(deviceId: string, licenseKey: string): Observable<void> {
    return this.api.post<void>('/license/deactivate-device', { deviceId, licenseKey }).pipe(map(() => void 0));
  }

  startTrial(): Observable<LicenseInfo> {
    return this.api.post<LicenseInfo>('/license/start-trial', {}).pipe(map(res => res.data));
  }

  upgradeLicense(planName: string, billingCycle: 'monthly' | 'yearly'): Observable<LicenseInfo> {
    return this.api.post<LicenseInfo>('/license/upgrade', { planName, billingCycle }).pipe(
      map(res => res.data),
      tap(data => {
        this.license.set(data);
        this.saveToCache(data);
      })
    );
  }

  clearLicense(): void {
    this.license.set(null);
    localStorage.removeItem(this.CACHE_KEY);
  }

  // ── Cache helpers (private) ──────────

  private saveToCache(license: LicenseInfo): void {
    const cacheData = {
      timestamp: Date.now(),
      data: license
    };
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      console.error('Failed to save license to cache', e);
    }
  }

  private loadFromCache(): LicenseInfo | null {
    try {
      const stored = localStorage.getItem(this.CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.data as LicenseInfo;
      }
    } catch (e) {
      console.error('Failed to load license from cache', e);
    }
    return null;
  }

  private isCacheValid(): boolean {
    try {
      const stored = localStorage.getItem(this.CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return (Date.now() - parsed.timestamp) <= this.CACHE_TTL;
      }
    } catch (e) {}
    return false;
  }

  // ── Admin methods ────────────────────

  generateLicense(req: GenerateLicenseRequest): Observable<LicenseInfo> {
    return this.api.post<LicenseInfo>('/admin/licenses/generate', req).pipe(map(res => res.data));
  }

  getAllLicenses(page: number, size: number): Observable<{ content: AdminLicense[], totalElements: number }> {
    return this.api.get<{ content: AdminLicense[], totalElements: number }>(`/admin/licenses?page=${page}&size=${size}`).pipe(map(res => res.data));
  }

  getPlatformStats(): Observable<LicensePlatformStats> {
    return this.api.get<LicensePlatformStats>('/admin/licenses/stats').pipe(map(res => res.data));
  }

  suspendLicense(id: string, reason: string): Observable<LicenseInfo> {
    return this.api.patch<LicenseInfo>(`/admin/licenses/${id}/suspend`, { reason }).pipe(map(res => res.data));
  }

  reactivateLicense(id: string): Observable<LicenseInfo> {
    return this.api.patch<LicenseInfo>(`/admin/licenses/${id}/reactivate`, {}).pipe(map(res => res.data));
  }

  extendLicense(id: string, days: number): Observable<LicenseInfo> {
    return this.api.patch<LicenseInfo>(`/admin/licenses/${id}/extend`, { days }).pipe(map(res => res.data));
  }
}
